import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-fe721432"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "distribution-content"
}
const _hoisted_2 = { class: "chart-wrapper" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignPieOption } from '@/utils/echarts-option/pie'
import { getCssColorVar } from '@/utils/chart-theme-colors'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import {
  getOverviewPieSliceColor,
  getThreatTypeNamedColors,
} from '@/views/dashboard/overview/_components/overview-pie-distribution'
import { pieColorForRiskTrendType } from './alert-trend-series-colors'
import type { ThreatTypeDistribution } from './realtime-monitoring.service'
import { HomeService, RiskEventTrendResponseData } from '@/views/dashboard/home/home.service'
import { getTimeRangeByEnum } from '@/utils/time'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import dayjs from 'dayjs'

const PORT_MANAGE_PATH = '/dashboard/port-manage'
const LOARIS_TROJAN_REMOVER_PATH = '/dashboard/loaris-trojan-remover'
const BASELINE_PATH = '/dashboard/base-line'
const VULNERABILITY_FIX_PATH = '/dashboard/vulnerability-fix'


export default /*#__PURE__*/_defineComponent({
  __name: 'threat-distribution-card',
  setup(__props) {

const { t, locale } = useI18n()
const router = useRouter()

function goDrawerPageByThreatType (threatType: string) {
  const routeByThreatType: Record<string, { path: string; query: Record<string, string> }> = {
    risk_port: {
      path: PORT_MANAGE_PATH,
      query: { open_port_list_drawer: '1' },
    },
    risk_virus: {
      path: LOARIS_TROJAN_REMOVER_PATH,
      query: { open_risk_drawer: '1' },
    },
    risk_baseline: {
      path: BASELINE_PATH,
      query: { open_check_item_drawer: '1' },
    },
    risk_leak: {
      path: VULNERABILITY_FIX_PATH,
      query: { open_leak_drawer: '1' },
    },
    risk_ransomware: {
      path: VULNERABILITY_FIX_PATH,
      query: { open_leak_drawer: '1' },
    },
  }
  const target = routeByThreatType[threatType]
  if (!target) return
  void router.push(target).catch(() => {})
}

/** 与左侧告警趋势「风险视图」图例文案一致（alert-trend-card SERIES_FIELDS nameKeyRisk） */
const RISK_DISTRIBUTION_TYPE_I18N_KEYS: Record<string, string> = {
  risk_ransomware: 'overviewZone.alertTrendLegendRiskRansomware',
  risk_virus: 'overviewZone.alertTrendLegendRiskVirus',
  risk_leak: 'overviewZone.alertTrendLegendRiskVulnerability',
  risk_baseline: 'overviewZone.alertTrendLegendRiskBaseline',
  risk_port: 'overviewZone.alertTrendLegendRiskPort',
}

function threatTypeLabel (type: string) {
  const key = RISK_DISTRIBUTION_TYPE_I18N_KEYS[type]
  return key ? t(key) : type
}

function formatPiePercent (raw: unknown): string {
  const n = Number(raw)
  if (!Number.isFinite(n)) return String(raw ?? '')
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

/** 近 30 天 `risk-event-trend` 按天序列，取最后一天各风险计数 → 饼图数据 */
function mapRiskEventTrendLastDay (body: RiskEventTrendResponseData): ThreatTypeDistribution[] {
  const lens = [
    body.time_points?.length ?? 0,
    body.ransomware_risk_counts?.length ?? 0,
    body.virus_risk_counts?.length ?? 0,
    body.leak_risk_counts?.length ?? 0,
    body.baseline_risk_counts?.length ?? 0,
    body.port_risk_counts?.length ?? 0,
  ]
  const n = Math.max(0, ...lens)
  if (n === 0) return []
  // 取“最近一个有数据”的时间点；若最后一天全为 0，向前回溯，避免 API 有历史数据但卡片空态
  let idx = n - 1
  for (let i = n - 1; i >= 0; i--) {
    const totalAtI =
      Number(body.ransomware_risk_counts?.[i] ?? 0) +
      Number(body.virus_risk_counts?.[i] ?? 0) +
      Number(body.leak_risk_counts?.[i] ?? 0) +
      Number(body.baseline_risk_counts?.[i] ?? 0) +
      Number(body.port_risk_counts?.[i] ?? 0)
    if (totalAtI > 0) {
      idx = i
      break
    }
  }
  const rows = [
    { type: 'risk_ransomware', count: Number(body.ransomware_risk_counts?.[idx] ?? 0) },
    { type: 'risk_virus', count: Number(body.virus_risk_counts?.[idx] ?? 0) },
    { type: 'risk_leak', count: Number(body.leak_risk_counts?.[idx] ?? 0) },
    { type: 'risk_baseline', count: Number(body.baseline_risk_counts?.[idx] ?? 0) },
    { type: 'risk_port', count: Number(body.port_risk_counts?.[idx] ?? 0) },
  ]
  const total = rows.reduce((a, r) => a + r.count, 0)
  return rows.map((r) => ({
    type: r.type,
    count: r.count,
    percentage: total > 0 ? Math.round((r.count / total) * 10000) / 100 : 0,
  }))
}

const injector = useReflectiveInjector([HomeService, ResizeService])
const homeService = injector.get(HomeService)
const resizeService = injector.get(ResizeService)

const rawData = ref<ThreatTypeDistribution[]>([])

function loadData () {
  const [st, et] = getTimeRangeByEnum(TimeRange.DAYS_30)
  const startSec = st ?? Math.floor(dayjs().subtract(30, 'day').valueOf() / 1000)
  const endSec = et ?? Math.floor(dayjs().valueOf() / 1000)
  homeService
    .getTerminalRiskEventTrend({
      start_time: String(startSec),
      end_time: String(endSec),
      granularity: 'day',
    })
    .then((res) => {
      rawData.value = mapRiskEventTrendLastDay(res)
    })
    .catch(() => {
      rawData.value = []
    })
}

watch(locale, () => {
  if (hasChartData.value) {
    nextTick(() => renderChart())
  }
})

useEchartsThemeSync(() => {
  if (chartInstance && hasChartData.value) renderChart()
})

onMounted(() => {
  loadData()
})

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

const hasChartData = computed(() => rawData.value.some(item => Number(item.count) > 0))

function onPieChartClick (raw: unknown) {
  const param = (Array.isArray(raw) ? raw[0] : raw) as { data?: { threatType?: string } }
  const threatType =
    param && param.data && typeof param.data.threatType === 'string'
      ? param.data.threatType
      : ''
  if (typeof threatType === 'string' && threatType) {
    goDrawerPageByThreatType(threatType)
  }
}

function initChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
  chartInstance.off('click')
  chartInstance.on('click', onPieChartClick)
  renderChart()
  nextTick(() => {
    chartInstance?.resize()
  })
}

function renderChart () {
  if (!chartInstance || !hasChartData.value) return

  const pieData = rawData.value.map((item, index) => ({
    name: threatTypeLabel(item.type),
    value: item.percentage,
    /** 扇区点击跳转响应中心时解析，勿删（与 RiskTrendResponseData 序列含义一致） */
    threatType: item.type,
    itemStyle: {
      color:
        pieColorForRiskTrendType(item.type) ??
        getOverviewPieSliceColor(item.type, index, getThreatTypeNamedColors()),
    },
  }))

  const option = {
    series: [{
      data: pieData,
      name: t('overviewZone.chartSeriesPercent'),
      /** 该卡片改为实心饼图：扇区填充到圆心 */
      radius: ['0%', '60%'],
      label: {
        show: true,
        color: getCssColorVar('--cm-color-text-primary', '#222'),
        formatter: (params: { name: string; data?: { realValue?: number }; value?: number }) => {
          const value =
            params && params.data && typeof params.data.realValue !== 'undefined'
              ? params.data.realValue
              : params.value
          const pct = formatPiePercent(value ?? 0)
          return `${params.name}：${pct}%`
        },
      },
    }],
  }

  const finalOption = getAssignPieOption(option, { unit: '%' })
  chartInstance.setOption(finalOption)
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance && chartInstance.resize()
})

watch(() => rawData.value, () => {
  if (!hasChartData.value) {
    chartInstance?.dispose()
    chartInstance = null
    return
  }
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance && chartInstance.dispose()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Card, {
    size: "second",
    title: _unref(t)('overviewZone.threatDistributionTitle'),
    class: "threat-distribution-card-wrap"
  }, {
    default: _withCtx(() => [
      (hasChartData.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", {
                ref_key: "chartRef",
                ref: chartRef,
                class: "chart"
              }, null, 512)
            ])
          ]))
        : (_openBlock(), _createBlock(EmptyData, {
            key: 1,
            height: "292px",
            "bg-color": "transparent"
          }))
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})